import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {DefaultTheme, useTheme} from 'styled-components';
import {cn, DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {BookingSummary, ShortReservation} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useCustomTheme} from 'context/customTheme';
import {useReservationSettings} from 'hooks';
import {CustomIcon} from '@guestapp/old-ui-kit';
import {ReservationNumberBox} from '../CheckInInfoBox/ReservationNumberBox';
import {
  checkInTimeSelector,
  checkOutTimeSelector,
  isNoHours,
  getTime,
} from 'utils/reservation/checkInOutTime';
import {
  Content,
  DateInfo as StyledDateInfo,
  DatesArrow,
  FullDate,
  Header,
  MainDate,
  Title,
  Wrapper,
} from './styled';

type DateInfoBoxProps = {
  title: string;
  mainDate?: string;
  fullDate?: string;
  isNoHours: boolean;
  className?: string;
};
function getFullDate(date: string) {
  return dayjs(date).utc().format('DD MMM YYYY').toUpperCase();
}

const getIconColor = (hasTemplate: boolean, isMobile: boolean, theme: DefaultTheme) => {
  if (!hasTemplate && !isMobile) {
    return '#152946';
  }
  if (isMobile) {
    return theme.colors.branding.boxesModals;
  }
  return theme.colors.branding.fontPrimary;
};

const getDatesInfo = (reservation?: ShortReservation, summary?: BookingSummary) => {
  if (!reservation || !summary) return {};

  return {
    checkIn: {
      mainDate: getTime(reservation, summary)(checkInTimeSelector),
      fullDate: getFullDate(reservation?.check_in_date),
    },
    checkOut: {
      mainDate: getTime(reservation, summary)(checkOutTimeSelector),
      fullDate: getFullDate(reservation?.check_out_date),
    },
  };
};

function BookingDatesBox() {
  const {t} = useTranslation();
  const {data: reservation} = useReservation();
  const {showReservationNumberBox} = useReservationSettings();
  const {data: summary} = useSummary();
  const {hasTemplate} = useCustomTheme();
  const theme = useTheme();
  const {isMatch: isMobile} = useScreenResize(DEVICE.laptopM);
  const datesInfo = getDatesInfo(reservation, summary);

  const iconColor = getIconColor(hasTemplate, isMobile, theme);

  return (
    <Wrapper
      $hasTemplate={hasTemplate}
      className="chekin-booking-box__container relative kiosko:py-5 kiosko:rounded-t-[28px] kiosko:rounded-b-[0px]"
    >
      <Header
        $hasTemplate={hasTemplate}
        className="chekin-booking-box__header kiosko:hidden"
      >
        {t('booking_dates')}
      </Header>
      <Content className="chekin-booking-box__content kiosko:h-auto kiosko:bg-inherit kiosko:p-0 kiosko:text-white">
        <DateInfoBox
          title={t('check_in_short')}
          mainDate={datesInfo.checkIn?.mainDate}
          fullDate={datesInfo.checkIn?.fullDate}
          isNoHours={isNoHours(summary)}
          className="chekin-booking-box__info-chekin"
        />
        <DatesArrow>
          <CustomIcon
            name="arrowDates"
            custom
            color={iconColor}
            className={cn(
              'chekin-booking-box__content-icon',
              'kiosko:w-[40px] kiosko:h-[40px]',
            )}
          />
        </DatesArrow>
        <DateInfoBox
          title={t('check_out_short')}
          mainDate={datesInfo.checkOut?.mainDate}
          fullDate={datesInfo.checkOut?.fullDate}
          isNoHours={isNoHours(summary)}
          className="chekin-booking-box__info-chekout"
        />
      </Content>
      {showReservationNumberBox && (
        <div className="hidden laptop-ml:block">
          <ReservationNumberBox />
        </div>
      )}
    </Wrapper>
  );
}

function DateInfoBox({
  title,
  mainDate,
  fullDate,
  isNoHours,
  className,
}: DateInfoBoxProps) {
  return (
    <StyledDateInfo className={cn(className, 'kiosko:gap-4')}>
      <Title
        className={cn(
          'chekin-booking-box__info-title',
          'kiosko:text-kiosko-xs kiosko:uppercase',
        )}
      >
        {title}
      </Title>
      <MainDate
        className={cn('chekin-booking-box__info-hours', 'kiosko:text-kiosko-s')}
        $isNoHours={isNoHours}
      >
        {mainDate}
      </MainDate>
      <FullDate
        $isNoHours={isNoHours}
        className={cn('chekin-booking-box__info-date', 'kiosko:text-kiosko-s')}
      >
        {fullDate}
      </FullDate>
    </StyledDateInfo>
  );
}

export {BookingDatesBox};
